import { useEffect, useRef } from "react";
import { Hero } from "../components/Hero";
import { AboutUs } from "../components/AboutUs";
import { About } from "../components/About";
import { Offerings } from "../components/Offerings";
import { Reviews } from "../components/Reviews";
import { Testimonials } from "../components/Testimonials";
import { WhatToBring } from "../components/WhatToBring";
import { Faq } from "../components/Faq";
import { AddOns } from "../components/AddOns";
import { Hours } from "../components/Hours";
import { Contact } from "../components/Contact";
import { MapEmbed } from "../components/MapEmbed";
import { ClosingCta } from "../components/ClosingCta";
import { AmbientAudio } from "../components/AmbientAudio";
import { business, DEMO_CONTENT } from "../data/business";

export default function Home() {
  // Round 3 (Agent 3A) item #2: thin gold scroll-progress bar on mobile.
  // Reduced-motion users opt out; we also passive-listen and rAF-throttle
  // so the listener doesn't fight the rest of the page on touch devices.
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${business.name} — Whitewater Rafting · Lodging · Dining · Benton, TN`;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      const el = progressRef.current;
      if (!el) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      const max =
        (document.documentElement.scrollHeight || 0) -
        (window.innerHeight || 0);
      const pct = max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0;
      el.style.setProperty("--scroll-progress", String(pct));
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Sections built on placeholder data only render when DEMO_CONTENT is on.
  // - Testimonials: fictional reviewers + invented guide bios
  // - AddOns:       prices and "weekend combo" not confirmed by the business
  return (
    <>
      {/* The hero's headline is rendered into the cinematic background video,
          so the DOM has no visible <h1>. Add a visually hidden one so the page
          still has exactly one <h1> for assistive tech and SEO. */}
      <h1 className="sr-only">
        {business.name} — Whitewater rafting, lodging, and dining on the Ocoee
      </h1>
      {/* Round 3 (Agent 3A) item #2: scroll-progress bar.
          Visible at ≤900px only (CSS-gated); --scroll-progress is driven
          by the rAF-throttled scroll listener above. */}
      <div
        ref={progressRef}
        className="home-scroll-progress"
        aria-hidden="true"
        role="presentation"
      />
      <Hero />
      {/* Russ feedback (2026-05-24): removed <QuickBook /> from the home
          flow. The widget pushed About Us below the fold on mobile and
          duplicated the Book CTA that lives in Hero. Booking still works
          through the Hero CTA, header pill, MobileBottomBar, and the
          per-product Detail pages — all of which deep-link to the right
          Flybook entity. */}
      <AboutUs />
      {/* Russ feedback 2026-05-24: swap the Offerings ("Pick your
          adventure") with the rafting deep-dive <About /> so the flow
          goes: brand-statement → product cards → rafting story → reviews.
          Lets visitors who scrolled past AboutUs see the offering grid
          immediately, then dive into the rafting narrative below. */}
      <Offerings />
      <About />
      {/* Real Google reviews — renders nothing until `reviews` array in
          business.ts is populated by the operator (Russ). Now sits AFTER
          the rafting deep-dive so social proof closes the narrative
          before the practical "What to Bring" list begins. */}
      <Reviews />
      {DEMO_CONTENT && <AddOns />}
      {DEMO_CONTENT && <Testimonials />}
      <WhatToBring />
      <Faq />
      <Hours />
      <Contact />
      <MapEmbed />
      {/* Pre-footer conversion block — addresses the "where do I go next?"
          gap on long pages. Two paths: external Book + tel: call. */}
      <ClosingCta />
      {/* Ambient river-flow audio as a floating toggle.
          Default OFF — user opts in via click (browser autoplay policy
          blocks unsolicited sound anyway). Preference persists per browser. */}
      <AmbientAudio />
    </>
  );
}
